import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  bucketAlertTransitions,
  bucketSessions,
} from "./unitEventsUtils";

export type TrendRange = "week" | "month" | "3month" | "year" | "all";
export type TrendView =
  | "alerts"
  | "sessions"
  | "alertsPerSession"
  | "resolvedRate";

type Props = {
  /** Cached alarm records used for the smaller ranges (week/month/3-months). */
  records: any[];
  /** Optional larger pull used for year/all-time. */
  historyRecords?: any[];
  historyLoading?: boolean;
  historyCap?: number | null;
  /** Cooking sessions for the same sensor/portfolio (small pull). */
  sessions?: any[];
  /** Wider session pull for year / all-time views. */
  historySessions?: any[];
  title?: string;
  defaultRange?: TrendRange;
  defaultView?: TrendView;
};

const RANGES: ReadonlyArray<readonly [TrendRange, string]> = [
  ["week", "Past week"],
  ["month", "Past month"],
  ["3month", "Past 3 months"],
  ["year", "Past year"],
  ["all", "All time"],
];

const VIEWS: ReadonlyArray<readonly [TrendView, string]> = [
  ["alerts", "Alerts"],
  ["sessions", "Cooking sessions"],
  ["alertsPerSession", "Alerts / session"],
  ["resolvedRate", "Resolved %"],
];

type SeriesDef = { dataKey: string; name: string; color: string };

const ALERT_SERIES: SeriesDef[] = [
  { dataKey: "isUnattended", name: "Unattended", color: "#FB8C00" },
  { dataKey: "isTooHot", name: "Dangerous temperature", color: "#E74C3C" },
  { dataKey: "isImmediateDanger", name: "Immediate danger", color: "#B00020" },
  { dataKey: "isStoveShutoff", name: "Auto shut-offs", color: "#1976D2" },
];

function ClickableLegend({
  series,
  hidden,
  onToggle,
}: {
  series: SeriesDef[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-3">
      {series.map((s) => {
        const isHidden = hidden.has(s.dataKey);
        return (
          <button
            key={s.dataKey}
            type="button"
            onClick={() => onToggle(s.dataKey)}
            title="Click to show or hide this series"
            className={[
              "inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-control",
              "transition-colors hover:bg-black/[0.04]",
              "cursor-pointer select-none",
              isHidden ? "opacity-40 line-through" : "",
            ].join(" ")}
            aria-pressed={!isHidden}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            {s.name}
          </button>
        );
      })}
    </div>
  );
}

export default function AlertTrendsCard({
  records,
  historyRecords,
  historyLoading,
  historyCap,
  sessions,
  historySessions,
  title = "Trends",
  defaultRange = "month",
  defaultView = "alerts",
}: Props) {
  const [range, setRange] = useState<TrendRange>(defaultRange);
  const [view, setView] = useState<TrendView>(defaultView);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const needsHistory = range === "year" || range === "all";
  const sourceRecords = useMemo(
    () => (needsHistory && historyRecords ? historyRecords : records),
    [needsHistory, historyRecords, records]
  );
  const sourceSessions = useMemo(
    () =>
      needsHistory && historySessions ? historySessions : sessions ?? [],
    [needsHistory, historySessions, sessions]
  );

  const alertBuckets = useMemo(
    () => bucketAlertTransitions(sourceRecords, range),
    [sourceRecords, range]
  );
  const sessionBuckets = useMemo(
    () => bucketSessions(sourceSessions, range),
    [sourceSessions, range]
  );

  // Combine into one row-per-bucket structure that all view modes can read.
  const data = useMemo(() => {
    const map = new Map<string, any>();
    alertBuckets.forEach((b) => {
      map.set(b.bucket, { ...b, sessions: 0 });
    });
    sessionBuckets.forEach((b) => {
      const existing = map.get(b.bucket);
      if (existing) existing.sessions = b.sessions;
      else
        map.set(b.bucket, {
          bucket: b.bucket,
          label: b.label,
          isUnattended: 0,
          isTooHot: 0,
          isImmediateDanger: 0,
          isStoveShutoff: 0,
          sessions: b.sessions,
        });
    });
    const out = Array.from(map.values());
    return out.map((b) => {
      const totalAlerts =
        b.isUnattended + b.isTooHot + b.isImmediateDanger;
      const alertsPerSession =
        b.sessions > 0 ? Number((totalAlerts / b.sessions).toFixed(2)) : null;
      // "Resolved" = alarms that did NOT trigger an automatic shutoff
      // (i.e. a caregiver responded and fixed the situation before the
      // system had to step in).
      const resolvedRate =
        totalAlerts > 0
          ? Math.max(
              0,
              Math.round(
                ((totalAlerts - b.isStoveShutoff) / totalAlerts) * 100
              )
            )
          : null;
      return { ...b, totalAlerts, alertsPerSession, resolvedRate };
    });
  }, [alertBuckets, sessionBuckets]);

  const cappedNote =
    needsHistory &&
    historyCap &&
    Array.isArray(historyRecords) &&
    historyRecords.length >= historyCap
      ? `Showing the most recent ${historyCap} alarm records.`
      : null;

  const toggleSeries = (key: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleAlertSeries = ALERT_SERIES.filter(
    (s) => !hidden.has(s.dataKey)
  );

  // Per-view: do we have anything to show?
  const hasData = (() => {
    if (view === "alerts") {
      return data.some((b) =>
        visibleAlertSeries.some((s) => (b as any)[s.dataKey] > 0)
      );
    }
    if (view === "sessions") return data.some((b) => b.sessions > 0);
    if (view === "alertsPerSession")
      return data.some((b) => b.alertsPerSession != null);
    if (view === "resolvedRate")
      return data.some((b) => b.resolvedRate != null);
    return false;
  })();

  return (
    <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <div
            role="group"
            aria-label="View"
            className="inline-flex rounded-control border border-black/10 bg-black/[0.03] p-0.5 text-sm"
          >
            {VIEWS.map(([key, label]) => {
              const active = view === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={[
                    "px-3 py-1.5 rounded-control transition-colors",
                    active
                      ? "bg-primary/20 text-ink font-semibold border border-primary/45"
                      : "text-ink-2 hover:text-ink",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div
            role="group"
            aria-label="Trend range"
            className="inline-flex rounded-control border border-black/10 bg-black/[0.03] p-0.5 text-sm"
          >
            {RANGES.map(([key, label]) => {
              const active = range === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRange(key)}
                  className={[
                    "px-3 py-1.5 rounded-control transition-colors",
                    active
                      ? "bg-primary/20 text-ink font-semibold border border-primary/45"
                      : "text-ink-2 hover:text-ink",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {needsHistory && historyLoading && !historyRecords ? (
        <div className="text-ink-3 text-sm py-12 text-center">
          Loading history…
        </div>
      ) : !hasData ? (
        <div className="text-ink-3 text-sm py-12 text-center">
          No data in this range.
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            {view === "alerts" ? (
              <BarChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  width={28}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [value, name]}
                  cursor={{ fill: "rgba(187,218,98,0.12)" }}
                />
                {visibleAlertSeries.map((s) => (
                  <Bar
                    key={s.dataKey}
                    dataKey={s.dataKey}
                    name={s.name}
                    stackId="a"
                    fill={s.color}
                  />
                ))}
              </BarChart>
            ) : view === "sessions" ? (
              <BarChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  width={28}
                />
                <Tooltip
                  formatter={(value: number) => [value, "Sessions"]}
                  cursor={{ fill: "rgba(187,218,98,0.12)" }}
                />
                <Bar dataKey="sessions" name="Sessions" fill="#BBDA62" />
              </BarChart>
            ) : view === "alertsPerSession" ? (
              <LineChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  width={36}
                  allowDecimals
                />
                <Tooltip
                  formatter={(value: number) => [value, "Alerts / session"]}
                />
                <Line
                  type="monotone"
                  dataKey="alertsPerSession"
                  stroke="#E74C3C"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={true}
                />
              </LineChart>
            ) : (
              <LineChart
                data={data}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <CartesianGrid stroke="rgba(0,0,0,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 11, fill: "#4D4D4D" }}
                  width={40}
                />
                <Tooltip
                  formatter={(value: number) => [
                    `${value}%`,
                    "Alarms resolved",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="resolvedRate"
                  stroke="#388E3C"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={true}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
      {view === "alerts" && (
        <>
          <ClickableLegend
            series={ALERT_SERIES}
            hidden={hidden}
            onToggle={toggleSeries}
          />
          <div className="text-ink-3 text-xs text-center mt-1">
            Click a series to show or hide it.
          </div>
        </>
      )}
      {cappedNote && (
        <div className="text-ink-3 text-xs mt-2 text-center">{cappedNote}</div>
      )}
    </div>
  );
}
